import React, { useState } from "react";
import "./cssBoiler.css";
import "./ModernSkillsSection.css";
import { HiOutlineBolt, HiOutlineRocketLaunch } from "react-icons/hi2";
import {
  SiJavascript as JSIcon,
  SiNginx,
  SiReact,
  SiNodedotjs,
  SiTailwindcss,
  SiExpress,
  SiMongodb,
  SiMysql,
  SiRedis,
  SiCloudflare,
  SiDocker,
  SiMicrogenetics,
} from "react-icons/si";

function ModernSkillsSection() {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  const skills = [
    {
      id: 1,
      name: "JavaScript",
      category: "frontend",
      icon: JSIcon,
      description:
        "Strong command over JavaScript — from core concepts to advanced patterns like closures, async/await, and event-driven architecture across both frontend and backend.",
      proficiency: 95,
      gradient: "linear-gradient(135deg, #f7df1e 0%, #f0db4f 100%)",
      color: "#f7df1e",
    },
    {
      id: 2,
      name: "React",
      category: "frontend",
      icon: SiReact,
      description:
        "Proficient in modern React — advanced hooks, context API, optimized form handling, and scalable component architecture. I build fast, maintainable UIs using best practices and performance-focused techniques.",
      proficiency: 90,
      gradient: "linear-gradient(135deg, #61dafb 0%, #21d4fd 100%)",
      color: "#61dafb",
    },
    {
      id: 3,
      name: "Tailwind CSS",
      category: "frontend",
      icon: SiTailwindcss,
      description:
        "Tailwind CSS is my go-to for styling — it streamlines my workflow by enabling utility-first design directly in markup. I use it to craft clean, responsive, and modern UIs efficiently.",
      proficiency: 88,
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      color: "#06b6d4",
    },
    {
      id: 4,
      name: "Node.js",
      category: "backend",
      icon: SiNodedotjs,
      description:
        "After mastering frontend development, I dove deep into Node.js — building full-stack apps using core modules like fs, http, child_process, and cluster. I've developed scalable APIs, CLI tools, and handled real-world backend scenarios with performance and reliability in focus.",
      proficiency: 92,
      gradient: "linear-gradient(135deg, #339933 0%, #2d7d32 100%)",
      color: "#339933",
    },
    {
      id: 5,
      name: "Express.js",
      category: "backend",
      icon: SiExpress,
      description:
        "Skilled in building fast and scalable APIs using Express.js — with structured routing, middleware, and robust error handling in production-ready applications.",
      proficiency: 90,
      gradient: "linear-gradient(135deg, #000000 0%, #333333 100%)",
      color: "#000000",
    },
    {
      id: 6,
      name: "MySQL",
      category: "database",
      icon: SiMysql,
      description:
        "Proficient in MySQL — designing normalized schemas, writing complex queries, optimizing performance, and managing databases with PHPMyAdmin for scalable, data-driven applications.",
      proficiency: 85,
      gradient: "linear-gradient(135deg, #336791 0%, #2c5aa0 100%)",
      color: "#336791",
    },
    {
      id: 7,
      name: "MongoDB",
      category: "database",
      icon: SiMongodb,
      description:
        "Experienced with MongoDB — designing flexible schemas, optimizing queries, and handling large-scale data using Mongoose and the aggregation framework.",
      proficiency: 87,
      gradient: "linear-gradient(135deg, #47a248 0%, #388e3c 100%)",
      color: "#47a248",
    },
    {
      id: 8,
      name: "Redis",
      category: "backend",
      icon: SiRedis,
      description:
        "Proficient in using Redis for caching and session management — improving application speed, efficiency, and overall backend performance.",
      proficiency: 82,
      gradient: "linear-gradient(135deg, #dc382d 0%, #b71c1c 100%)",
      color: "#dc382d",
    },
    {
      id: 9,
      name: "BullMQ",
      category: "backend",
      icon: SiDocker,
      description:
        "Proficient in BullMQ for building robust job queues and background processing — enabling reliable task scheduling, job management, and handling high-throughput data processing efficiently.",
      proficiency: 80,
      gradient: "linear-gradient(135deg, #231f20 0%, #000000 100%)",
      color: "#231f20",
    },
    {
      id: 10,
      name: "Azure",
      category: "cloud",
      icon: SiCloudflare,
      description:
        "Hands-on experience with Azure VMs, Azure Blob Storage, and Azure CDN — building scalable cloud infrastructure, managing storage solutions, and optimizing content delivery for production applications.",
      proficiency: 85,
      gradient: "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
      color: "#0078d4",
    },
    {
      id: 11,
      name: "Microservices",
      category: "architecture",
      icon: SiMicrogenetics,
      description:
        "Experienced in designing and developing microservices-based backends — with a focus on scalability, modularity, and clean communication using REST, BullMQ, and Redis.",
      proficiency: 85,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#667eea",
    },
    {
      id: 12,
      name: "Nginx",
      category: "cloud",
      icon: SiNginx,
      description:
        "Used it for hosting React static files and Node.js apps. Experienced in configuring reverse proxy, load balancing, SSL termination, and serving static assets efficiently.",
      proficiency: 80,
      gradient: "linear-gradient(135deg, #009639 0%, #00a86b 100%)",
      color: "#009639",
    },
  ];

  const categories = [
    { id: "all", name: "All Skills", count: skills.length },
    {
      id: "frontend",
      name: "Frontend",
      count: skills.filter((s) => s.category === "frontend").length,
    },
    {
      id: "backend",
      name: "Backend",
      count: skills.filter((s) => s.category === "backend").length,
    },
    {
      id: "database",
      name: "Database",
      count: skills.filter((s) => s.category === "database").length,
    },
    {
      id: "cloud",
      name: "Cloud",
      count: skills.filter((s) => s.category === "cloud").length,
    },
    {
      id: "architecture",
      name: "Architecture",
      count: skills.filter((s) => s.category === "architecture").length,
    },
  ];

  const filteredSkills =
    activeCategory === "all"
      ? skills
      : skills.filter((skill) => skill.category === activeCategory);

  return (
    <div className="modern-skills-section" id="skills">
      <div className="skills-container">
        <div className="skills-header">
          <div className="section-badge">
            <span>Technical Expertise</span>
          </div>
          <h2 className="skills-title">
            Core <span className="gradient-text">Skills</span>
          </h2>
          <p className="skills-subtitle">
            A comprehensive overview of my technical stack and proficiency
            levels across different domains
          </p>
        </div>

        <div className="skills-categories">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${
                activeCategory === category.id ? "active" : ""
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              <span className="category-name">{category.name}</span>
              <span className="category-count">{category.count}</span>
            </button>
          ))}
        </div>

        <div className="skills-grid">
          {filteredSkills.map((skill, index) => (
            <div
              key={skill.id}
              className={`skill-card ${
                hoveredSkill === skill.id ? "hovered" : ""
              }`}
              onMouseEnter={() => setHoveredSkill(skill.id)}
              onMouseLeave={() => setHoveredSkill(null)}
              style={{ "--delay": `${index * 0.1}s` }}
            >
              <div className="skill-card-inner">
                <div className="skill-header">
                  <div
                    className="skill-icon-container"
                    style={{ background: skill.gradient }}
                  >
                    <skill.icon className="skill-icon" />
                  </div>
                  <div className="skill-info">
                    <h3 className="skill-name">{skill.name}</h3>
                    <div className="skill-category">{skill.category}</div>
                  </div>
                </div>

                <div className="skill-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${skill.proficiency}%`,
                        background: skill.gradient,
                      }}
                    ></div>
                  </div>
                  <span className="proficiency-text">{skill.proficiency}%</span>
                </div>

                <p className="skill-description">{skill.description}</p>

                <div className="skill-footer">
                  <div className="skill-stats">
                    <div className="stat">
                      <HiOutlineBolt className="stat-icon" />
                      <span className="stat-text">Expert</span>
                    </div>
                    <div className="stat">
                      <HiOutlineRocketLaunch className="stat-icon" />
                      <span className="stat-text">Production Ready</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ModernSkillsSection;
